import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  InputAdornment,
  Grid,
  Select,
  MenuItem,
  FormControl,
  Button,
  Stack,
} from "@mui/material";
import { Search, TrendingUp, TrendingDown, Minus, Download } from "lucide-react";
import { collection, getDocs } from "firebase/firestore";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { db } from "../firebase-config";
import Header from "../components/header";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

dayjs.extend(isBetween);

const ItemsSaleAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [inventoryData, setInventoryData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [downloading, setDownloading] = useState(null);
  
  // Default to today
  const [filterType, setFilterType] = useState("today");
  const [selectedRestaurant, setSelectedRestaurant] = useState("all");
  const [selectedItem, setSelectedItem] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  
  // Custom dates
  const [customStart, setCustomStart] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [customEnd, setCustomEnd] = useState(dayjs().endOf("month").format("YYYY-MM-DD"));

  // Month to Month
  const [compareMonth1, setCompareMonth1] = useState(dayjs().format("YYYY-MM"));
  const [compareMonth2, setCompareMonth2] = useState(dayjs().subtract(1, "month").format("YYYY-MM"));

  // Keep raw data in state
  const [rawInventory, setRawInventory] = useState([]);
  const [rawInvoices, setRawInvoices] = useState([]);
  const [rawCategories, setRawCategories] = useState([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (rawInventory.length > 0) {
      calculateAnalytics();
    }
  }, [filterType, customStart, customEnd, compareMonth1, compareMonth2, rawInventory, rawInvoices, selectedRestaurant]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const inventorySnapshot = await getDocs(collection(db, "inventoryItems"));
      const inventoryItems = inventorySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));

      const invoicesSnapshot = await getDocs(collection(db, "invoices"));
      const invoices = invoicesSnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));

      const categoriesSnapshot = await getDocs(collection(db, "inventoryCategory"));
      const categories = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        category: doc.data().category
      }));

      setRawInventory(inventoryItems);
      setRawInvoices(invoices);
      setRawCategories(categories);
    } catch (error) {
      console.error("Error fetching analytics data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get display date range based on filter
  const getDisplayDateRange = () => {
    const now = dayjs();
    switch (filterType) {
      case "today":
        return { start: now.startOf("day"), end: now.endOf("day") };
      case "yesterday":
        return { start: now.subtract(1, "day").startOf("day"), end: now.subtract(1, "day").endOf("day") };
      case "last3Days":
        return { start: now.subtract(2, "day").startOf("day"), end: now.endOf("day") };
      case "thisWeek":
        return { start: now.startOf("week"), end: now.endOf("week") };
      case "thisMonth":
        return { start: now.startOf("month"), end: now.endOf("month") };
      case "custom":
        return { start: dayjs(customStart).startOf("day"), end: dayjs(customEnd).endOf("day") };
      case "monthToMonth":
        return { 
          start: dayjs(`${compareMonth1}-01`).startOf("month"), 
          end: dayjs(`${compareMonth1}-01`).endOf("month") 
        };
      default:
        return { start: now.startOf("day"), end: now.endOf("day") };
    }
  };

  const calculateAnalytics = () => {
    let p1Start, p1End, p2Start, p2End;

    if (filterType === "monthToMonth") {
      p1Start = dayjs(`${compareMonth1}-01`).startOf("month");
      p1End = dayjs(`${compareMonth1}-01`).endOf("month");
      p2Start = dayjs(`${compareMonth2}-01`).startOf("month");
      p2End = dayjs(`${compareMonth2}-01`).endOf("month");
    } else {
      const displayRange = getDisplayDateRange();
      p1Start = displayRange.start;
      p1End = displayRange.end;

      const durationMs = p1End.diff(p1Start) + 1;
      p2End = p1Start.subtract(1, "millisecond");
      p2Start = p2End.subtract(durationMs, "millisecond").add(1, "millisecond");
    }

    const itemsMap = {};
    rawInventory.forEach((item) => {
      itemsMap[item.title] = {
        ...item,
        currentQty: 0,
        previousQty: 0,
      };
    });

    rawInvoices.forEach((invoice) => {
      if (selectedRestaurant !== "all" && invoice.restaurantName !== selectedRestaurant) {
        return;
      }

      const createdAt = dayjs(invoice.createdAt);
      const items = invoice.items || [];

      const isCurrent = createdAt.isBetween(p1Start, p1End, null, '[]');
      const isPrevious = createdAt.isBetween(p2Start, p2End, null, '[]');

      if (isCurrent || isPrevious) {
        items.forEach((item) => {
          if (itemsMap[item.title]) {
            if (isCurrent) {
              itemsMap[item.title].currentQty += Number(item.quantity || 0);
            }
            if (isPrevious) {
              itemsMap[item.title].previousQty += Number(item.quantity || 0);
            }
          }
        });
      }
    });

    setInventoryData(Object.values(itemsMap));
  };

  const filteredData = inventoryData.filter((item) => {
    const matchesSearch = item.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesItem = selectedItem === "all" || item.title === selectedItem;
    const matchesCategory = selectedCategory === "all" || item.categoryId === selectedCategory;
    return matchesSearch && matchesItem && matchesCategory;
  });

  const restaurantsList = Array.from(new Set(rawInvoices.map(i => i.restaurantName))).filter(Boolean).sort();
  // If a category is selected, filter itemsList to only show items in that category
  const filteredInventoryForItemsList = selectedCategory === "all" 
    ? rawInventory 
    : rawInventory.filter(i => i.categoryId === selectedCategory);
  const itemsList = Array.from(new Set(filteredInventoryForItemsList.map(i => i.title))).filter(Boolean).sort();

  // ========== DOWNLOAD FUNCTIONS ==========

  const getDateLabel = () => {
    const { start, end } = getDisplayDateRange();
    return `${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')}`;
  };

  const getActiveFiltersLabel = () => {
    const parts = [];
    if (selectedRestaurant !== "all") parts.push(`Restaurant: ${selectedRestaurant}`);
    if (selectedCategory !== "all") {
      const catName = rawCategories.find(c => c.id === selectedCategory)?.category || selectedCategory;
      parts.push(`Category: ${catName}`);
    }
    if (selectedItem !== "all") parts.push(`Item: ${selectedItem}`);
    return parts.length > 0 ? parts.join(" | ") : "All Restaurants | All Categories | All Items";
  };

  const downloadGridExcel = async () => {
    setDownloading('grid-excel');
    try {
      const workbook = XLSX.utils.book_new();
      const worksheetData = [];

      worksheetData.push([`Items Sale Analytics - Grid`]);
      worksheetData.push([`Date Range: ${getDateLabel()}`]);
      worksheetData.push([`Filters: ${getActiveFiltersLabel()}`]);
      worksheetData.push([]);
      worksheetData.push(["Item Title", "Price (£)", "Selected Qty", "Previous Qty", "Difference", "Growth %"]);

      filteredData.forEach(row => {
        const diff = row.currentQty - row.previousQty;
        let growthPercent = 0;
        if (row.previousQty > 0) {
          growthPercent = ((diff / row.previousQty) * 100).toFixed(1);
        } else if (row.currentQty > 0) {
          growthPercent = 100.0;
        }
        
        const growthText = growthPercent > 0 ? `+${growthPercent}%` : `${growthPercent}%`;
        const diffText = diff > 0 ? `+${diff}` : `${diff}`;
        
        worksheetData.push([
          row.title || "N/A",
          Number(row.price || 0).toFixed(2),
          row.currentQty,
          row.previousQty,
          diffText,
          growthText,
        ]);
      });

      worksheetData.push([]);
      worksheetData.push(["SUMMARY"]);
      worksheetData.push(["Total Items", filteredData.length]);
      worksheetData.push(["Total Current Qty", filteredData.reduce((s, r) => s + r.currentQty, 0)]);
      worksheetData.push(["Total Previous Qty", filteredData.reduce((s, r) => s + r.previousQty, 0)]);
      worksheetData.push(["Generated", new Date().toLocaleString()]);

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      worksheet["!cols"] = [{ wch: 30 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 14 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(workbook, worksheet, "Items Sale Analytics");
      XLSX.writeFile(workbook, `items_sale_analytics_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    } catch (err) {
      console.error("Excel download error:", err);
    } finally {
      setDownloading(null);
    }
  };

  const downloadGridPDF = async () => {
    setDownloading('grid-pdf');
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

      doc.setFontSize(20);
      doc.setTextColor(196, 22, 28);
      doc.text("Items Sale Analytics", doc.internal.pageSize.getWidth() / 2, 15, { align: "center" });

      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Date Range: ${getDateLabel()}`, 10, 23);
      doc.text(`Filters: ${getActiveFiltersLabel()}`, 10, 29);

      const headers = ["Item Title", "Price (£)", "Selected Qty", "Previous Qty", "Difference", "Growth %"];
      const rows = filteredData.map(row => {
        const diff = row.currentQty - row.previousQty;
        let growthPercent = 0;
        if (row.previousQty > 0) {
          growthPercent = ((diff / row.previousQty) * 100).toFixed(1);
        } else if (row.currentQty > 0) {
          growthPercent = 100.0;
        }
        
        const growthText = growthPercent > 0 ? `+${growthPercent}%` : `${growthPercent}%`;
        const diffText = diff > 0 ? `+${diff}` : `${diff}`;
        
        return [
          row.title || "N/A",
          `£${Number(row.price || 0).toFixed(2)}`,
          row.currentQty,
          row.previousQty,
          diffText,
          growthText,
        ];
      });

      autoTable(doc, {
        head: [headers],
        body: rows,
        startY: 34,
        theme: "grid",
        headStyles: { fillColor: [196, 22, 28], fontSize: 9, fontStyle: "bold", textColor: [255, 255, 255] },
        bodyStyles: { fontSize: 8 },
        alternateRowStyles: { fillColor: [248, 249, 250] },
      });

      // Summary page
      doc.addPage();
      doc.setFontSize(16);
      doc.setTextColor(196, 22, 28);
      doc.text("Summary", 14, 20);
      doc.setFontSize(11);
      doc.setTextColor(0);
      doc.text(`Total Items: ${filteredData.length}`, 14, 35);
      doc.text(`Total Current Period Qty: ${filteredData.reduce((s, r) => s + r.currentQty, 0)}`, 14, 45);
      doc.text(`Total Previous Period Qty: ${filteredData.reduce((s, r) => s + r.previousQty, 0)}`, 14, 55);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 65);

      doc.save(`items_sale_analytics_${dayjs().format('YYYY-MM-DD')}.pdf`);
    } catch (err) {
      console.error("PDF download error:", err);
    } finally {
      setDownloading(null);
    }
  };



  // ========== RENDER ==========

  return (
    <Box sx={{ backgroundColor: "#FAFAFA", minHeight: "100vh", p: { xs: 1, sm: 2, md: 3 } }}>
      <Header title="Items Sale Analytics" />
      
      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, backgroundColor: "white", border: "1px solid", borderColor: "divider" }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by item title or brand..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
            />
          </Grid>
          
          <Grid item xs={12} md={8}>
            <Box display="flex" gap={2} flexWrap="wrap" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} alignItems="center">
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={selectedRestaurant}
                  onChange={(e) => setSelectedRestaurant(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All Restaurants</MenuItem>
                  {restaurantsList.map((rest, idx) => (
                    <MenuItem key={idx} value={rest}>{rest}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    setSelectedItem("all"); // Reset item when category changes
                  }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {rawCategories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.category}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={selectedItem}
                  onChange={(e) => setSelectedItem(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">All Items</MenuItem>
                  {itemsList.map((item, idx) => (
                    <MenuItem key={idx} value={item}>{item}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="yesterday">Yesterday</MenuItem>
                  <MenuItem value="last3Days">Last 3 Days</MenuItem>
                  <MenuItem value="thisWeek">This Week</MenuItem>
                  <MenuItem value="thisMonth">This Month</MenuItem>
                  <MenuItem value="custom">Custom Date Range</MenuItem>
                  <MenuItem value="monthToMonth">Month to Month Comparison</MenuItem>
                </Select>
              </FormControl>

              {filterType === "custom" && (
                <Box display="flex" gap={1} alignItems="center">
                  <TextField
                    type="date"
                    size="small"
                    label="From"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 140, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                  <Typography variant="body2" color="textSecondary">-</Typography>
                  <TextField
                    type="date"
                    size="small"
                    label="To"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 140, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Box>
              )}

              {filterType === "monthToMonth" && (
                <Box display="flex" gap={1} alignItems="center">
                  <TextField
                    type="month"
                    size="small"
                    label="Selected Month"
                    value={compareMonth1}
                    onChange={(e) => setCompareMonth1(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 140, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                  <Typography variant="body2" color="textSecondary">vs</Typography>
                  <TextField
                    type="month"
                    size="small"
                    label="Previous Month"
                    value={compareMonth2}
                    onChange={(e) => setCompareMonth2(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 140, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", maxHeight: "55vh", mb: 3 }}>
            <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: "primary.light", fontWeight: 600 }}>Item Title</TableCell>
                <TableCell sx={{ backgroundColor: "primary.light", fontWeight: 600 }}>Price</TableCell>
                <TableCell sx={{ backgroundColor: "primary.light", fontWeight: 600, textAlign: "center" }}>
                  Selected Qty
                </TableCell>
                <TableCell sx={{ backgroundColor: "primary.light", fontWeight: 600, textAlign: "center" }}>
                  Previous Qty
                </TableCell>
                <TableCell sx={{ backgroundColor: "primary.light", fontWeight: 600, textAlign: "center" }}>Difference</TableCell>
                <TableCell sx={{ backgroundColor: "primary.light", fontWeight: 600, textAlign: "center" }}>Growth %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((row, index) => {
                const diff = row.currentQty - row.previousQty;
                let growthPercent = 0;
                if (row.previousQty > 0) {
                  growthPercent = ((diff / row.previousQty) * 100).toFixed(1);
                } else if (row.currentQty > 0) {
                  growthPercent = 100.0;
                }
                
                let trendColor = "text.secondary";
                let trendIcon = <Minus size={16} />;
                let diffText = "-";
                let growthText = "0%";
                
                if (diff > 0) {
                  trendColor = "success.main";
                  trendIcon = <TrendingUp size={16} />;
                  diffText = `+${diff}`;
                  growthText = `+${growthPercent}%`;
                } else if (diff < 0) {
                  trendColor = "error.main";
                  trendIcon = <TrendingDown size={16} />;
                  diffText = `${diff}`;
                  growthText = `${growthPercent}%`;
                } else {
                  diffText = "0";
                  growthText = "0%";
                }

                return (
                  <TableRow key={index} hover>
                    <TableCell>{row.title || "N/A"}</TableCell>
                    <TableCell>£{Number(row.price || 0).toFixed(2)}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: "bold", color: "primary.main" }}>{row.currentQty}</TableCell>
                    <TableCell align="center">{row.previousQty}</TableCell>
                    <TableCell align="center" sx={{ color: trendColor, fontWeight: 500 }}>
                      <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                        {trendIcon}
                        {diffText}
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ color: trendColor, fontWeight: 500 }}>
                      {growthText}
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    No items found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Charts */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                🔥 Top 10 Most Ordered Items
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: "block" }}>
                Based on: {filterType === "monthToMonth" ? `${dayjs(compareMonth1 + "-01").format("MMM YYYY")} vs ${dayjs(compareMonth2 + "-01").format("MMM YYYY")}` : filterType.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                {selectedRestaurant !== "all" ? ` | ${selectedRestaurant}` : ""}
                {selectedCategory !== "all" ? ` | ${rawCategories.find(c => c.id === selectedCategory)?.category || ""}` : ""}
              </Typography>
              <Box height={350}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[...filteredData].sort((a, b) => b.currentQty - a.currentQty).slice(0, 10)} margin={{ bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="title" angle={-35} textAnchor="end" interval={0} tick={{ fontSize: 10 }} height={80} />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="currentQty" name="Selected Qty" fill="#4caf50" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="previousQty" name="Previous Qty" fill="#90caf9" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                📉 Top 10 Least Selling Items
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: "block" }}>
                Based on: {filterType === "monthToMonth" ? `${dayjs(compareMonth1 + "-01").format("MMM YYYY")} vs ${dayjs(compareMonth2 + "-01").format("MMM YYYY")}` : filterType.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                {selectedRestaurant !== "all" ? ` | ${selectedRestaurant}` : ""}
                {selectedCategory !== "all" ? ` | ${rawCategories.find(c => c.id === selectedCategory)?.category || ""}` : ""}
              </Typography>
              <Box height={350}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[...filteredData].sort((a, b) => a.currentQty - b.currentQty).slice(0, 10)} margin={{ bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="title" angle={-35} textAnchor="end" interval={0} tick={{ fontSize: 10 }} height={80} />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="currentQty" name="Selected Qty" fill="#f44336" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="previousQty" name="Previous Qty" fill="#ffcdd2" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </>
      )}

      {/* Download Options */}
      {!loading && (
        <Paper elevation={0} sx={{ p: 3, mt: 3, borderRadius: 3, backgroundColor: "white", border: "1px solid", borderColor: "divider" }}>
          <Typography variant="h6" color="primary" fontWeight={600} gutterBottom>
            Download Options
          </Typography>

          <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
            Analytics Report Data
          </Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mb: 3 }}>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<Download size={16} />}
              onClick={downloadGridExcel}
              disabled={downloading === 'grid-excel'}
              sx={{ borderRadius: 5, textTransform: "none", fontWeight: 600 }}
            >
              {downloading === 'grid-excel' ? 'Downloading...' : 'Analytics Excel'}
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<Download size={16} />}
              onClick={downloadGridPDF}
              disabled={downloading === 'grid-pdf'}
              sx={{ borderRadius: 5, textTransform: "none", fontWeight: 600 }}
            >
              {downloading === 'grid-pdf' ? 'Downloading...' : 'Analytics PDF'}
            </Button>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default ItemsSaleAnalytics;
